import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { Pencil } from 'lucide-react';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { Form } from '@dashboard/components/Formik';
import { Button as UIButton } from '@dashboard/components/ui/button';
import { Card } from '@dashboard/components/ui/card';
import {
  COUPON_DURATION_LABEL,
  COUPON_DURATION_OPTIONS,
  getCouponDiscountLabel,
  getCouponDurationOptionLabel,
} from '@dashboard/components/DiscountsCoupons/couponLabels';
import Stepper from '@dashboard/components/ui/stepper';
import { trim } from 'lodash';
import { buildCouponBody, copyText, isCodeTaken, saveCoupon } from './helpers';
import { COUPON_STEPS, getCouponStepSchema } from './couponSchemas';
import { scopeFields } from '@dashboard/components/DiscountsCoupons/couponScope';
import { expiryFields, initialExpiry } from '@dashboard/components/DiscountsCoupons/couponExpiry';
import { DateTimeWithZone } from '@dashboard/components/TimeZoneLabel';
import Button from '@/dashboard/components/Button';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { toast } from '@dashboard/context/ToastContext';
import { confirm } from '@dashboard/lib/confirm';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import {
  CouponScopePage,
  SCOPE_LIST_HEADER,
} from '@dashboard/components/DiscountsCoupons/CouponScopeSummary';
import { EVERYWHERE_SENTENCE } from '@dashboard/components/DiscountsCoupons/couponRuleSentence';

const LAST_STEP = COUPON_STEPS.length;

const INITIAL_VALUES = {
  label: '',
  code: '',
  limitScope: 'everywhere',
  scopePages: [],
  discountType: 'amountOff',
  duration: 'forever',
  durationInMonths: 1,
  amountOff: null,
  percentOff: null,
  currency: 'USD',
  appliesToSetupFee: false,
  appliesToTicketFees: false,
  ...initialExpiry(null),
  maxRedemptions: null,
};

const SubsectionTitle = ({ children }) => (
  <h3 className="text-xs font-medium text-muted-foreground">{children}</h3>
);

const ReviewSection = ({ title, onEdit, children }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold">{title}</h2>
      <UIButton
        type="button"
        variant="ghost"
        size="sm"
        onClick={onEdit}
        aria-label={`Edit ${title.toLocaleLowerCase()}`}
        className="h-7 px-2 text-xs text-muted-foreground"
      >
        <Pencil className="size-3 mr-1.5" />
        Edit
      </UIButton>
    </div>
    <dl className="rounded-lg border border-border divide-y divide-border text-sm">{children}</dl>
  </div>
);

const ReviewRows = ({ rows }) =>
  Object.entries(rows).map(([label, value]) => (
    <div key={label} className="flex items-start justify-between gap-4 px-4 py-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-right">{value}</dd>
    </div>
  ));

const ScopeReviewRows = ({ pages, appliesToSetupFee, appliesToTicketFees }) => {
  if (!pages.length) {
    return <ReviewRows rows={{ 'Checkouts and events': EVERYWHERE_SENTENCE }} />;
  }
  return (
    <>
      {pages.length > 1 && (
        <div className="px-4 py-3 text-muted-foreground">{SCOPE_LIST_HEADER}</div>
      )}
      {pages.map(page => (
        <div key={page._id} className="px-4 py-3">
          <CouponScopePage
            page={page}
            nameAs="dt"
            sentenceAs="dd"
            appliesToSetupFee={appliesToSetupFee}
            appliesToTicketFees={appliesToTicketFees}
          />
        </div>
      ))}
    </>
  );
};

// Uses the coupon detail page's labels and wording, so the review reads like the coupon
// the merchant lands on after creating it.
const CouponReview = ({ onEdit }) => {
  const { values } = useFormikContext();
  const coupon = buildCouponBody(values);
  const limitedTo = coupon.rules.length ? values.scopePages : [];

  return (
    <section aria-label="Coupon review" className="space-y-6">
      <ReviewSection title="Coupon" onEdit={() => onEdit(1)}>
        <ReviewRows
          rows={{
            Label: coupon.label,
            Code: coupon.code,
            'Discount amount': getCouponDiscountLabel(coupon),
            ...(coupon.amountOff ? { Currency: coupon.currency?.toUpperCase() } : {}),
            [COUPON_DURATION_LABEL]: getCouponDurationOptionLabel(coupon),
            'Applies to setup fee': coupon.appliesToSetupFee ? 'Yes' : 'No',
            'Applies to booking fees': coupon.appliesToTicketFees ? 'Yes' : 'No',
          }}
        />
      </ReviewSection>
      <ReviewSection title="Limit usage" onEdit={() => onEdit(2)}>
        <ScopeReviewRows
          pages={limitedTo}
          appliesToSetupFee={coupon.appliesToSetupFee}
          appliesToTicketFees={coupon.appliesToTicketFees}
        />
        <ReviewRows
          rows={{
            'Max uses': coupon.maxRedemptions ? coupon.maxRedemptions : '∞',
            Expires: coupon.redeemBy ? (
              <DateTimeWithZone date={coupon.redeemBy} />
            ) : (
              "Doesn't expire"
            ),
          }}
        />
      </ReviewSection>
    </section>
  );
};

const CouponStepFooter = ({ step, onBack }) => (
  <div className="mt-4 flex items-center gap-4">
    {step > 1 && (
      <FormSubmit.Cancel size="full" onClick={onBack}>
        Back
      </FormSubmit.Cancel>
    )}
    <FormSubmit.Button
      size="full"
      className={step === 1 ? undefined : 'ml-auto w-auto'}
      submitSpinner={step === LAST_STEP}
    >
      {step < LAST_STEP ? 'Continue' : 'Create coupon'}
    </FormSubmit.Button>
  </div>
);

const DiscountsCouponsCreate = () => {
  const { sellerId, token } = useAuth();
  const history = useHistory();
  const [step, setStep] = useState(1);
  const [reachedStep, setReachedStep] = useState(1);
  const formikRef = useRef(null);
  const jumpTargetRef = useRef(null);

  const goToStep = n => {
    setStep(n);
    setReachedStep(reached => Math.max(reached, n));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // A forward jump submits the current step, so it is validated exactly as Continue would.
  const handleStepClick = async n => {
    if (n < step) {
      goToStep(n);
      return;
    }
    jumpTargetRef.current = n;
    try {
      await formikRef.current?.submitForm();
    } finally {
      jumpTargetRef.current = null;
    }
  };

  // Nothing is saved until the last step, so leaving after step 1 throws work away.
  const leave = async () => {
    if (reachedStep > 1) {
      const discard = await confirm('Discard unsaved changes?', { confirm: 'Discard' });
      if (!discard) return;
    }
    history.push('/coupons');
  };

  return (
    <div className="w-full max-w-screen-2xl mx-auto mb-36 px-4 sm:px-16 mt-8">
      <div className="absolute top-6 left-4">
        <Button appearance="light" onClick={leave} aria-label="Back to coupons">
          <ChevronLeftIcon className="w-5 h-5" />
        </Button>
      </div>

      <div className="w-full max-w-lg mx-auto mt-20 mb-20">
        <div className="mb-8 text-center space-y-1">
          <h1 className="text-xl text-balance">Create a coupon</h1>
          <p className="text-muted-foreground text-sm">
            Let customers redeem a discount at checkout
          </p>
        </div>

        <div className="mb-8">
          <Stepper
            steps={COUPON_STEPS}
            current={step}
            reached={reachedStep}
            onStepClick={handleStepClick}
          />
        </div>

        <Card className="p-5">
          <Form
            showState={false}
            innerRef={formikRef}
            validationSchema={getCouponStepSchema(step)}
            initialValues={INITIAL_VALUES}
            formSubmit={() => <CouponStepFooter step={step} onBack={() => goToStep(step - 1)} />}
            onSubmit={async values => {
              // Next and Enter submit the form on every step; only the last one creates.
              if (step < LAST_STEP) {
                if (step === 1 && (await isCodeTaken(trim(values.code), sellerId, token))) {
                  return {
                    error: {
                      type: 'ValidationError',
                      errors: [
                        { param: 'code', message: 'A coupon with this code already exists' },
                      ],
                    },
                  };
                }
                goToStep(jumpTargetRef.current ?? step + 1);
                return { data: values };
              }

              try {
                const body = buildCouponBody(values);
                const response = await saveCoupon(body, sellerId, token);

                if (!response.data) {
                  throw response;
                }

                // Before navigating: the clipboard only accepts writes from a focused page.
                const copied = await copyText(body.code);

                history.push(`/coupons/${response.data._id}`);
                toast({
                  title: 'Coupon created',
                  ...(copied && { description: 'Code copied to clipboard' }),
                  status: 'success',
                });

                return { data: response.data };
              } catch (err) {
                return { error: err.message ? err : 'Something went wrong' };
              }
            }}
            fields={({ values }) => {
              return [
                {
                  name: 'label',
                  label: 'Label',
                  required: true,
                  showRequired: true,
                  type: 'text',
                  placeholder: 'Coupon label',
                  helpText: 'Internal name for this coupon.',
                  visible: step === 1,
                },
                {
                  name: 'code',
                  label: 'Code',
                  required: true,
                  showRequired: true,
                  type: 'text',
                  helpText: 'The code customers enter to redeem.',
                  placeholder: 'Coupon code',
                  visible: step === 1,
                },
                { type: 'divider', id: 'discount-divider-top', visible: step === 1 },
                {
                  name: 'discountType',
                  required: true,
                  type: 'radioCard',
                  options: [
                    { label: 'Fixed amount', value: 'amountOff' },
                    { label: 'Percentage', value: 'percentOff' },
                  ],
                  visible: step === 1,
                },
                {
                  type: 'columns',
                  columns: 2,
                  gap: 'sm',
                  fields: [
                    {
                      name: 'amountOff',
                      label: 'Discount amount',
                      // The browser refuses to submit with an empty required input it has hidden.
                      required: step === 1 && values.discountType === 'amountOff',
                      showRequired: true,
                      type: 'number',
                      placeholder: 'Discount amount',
                    },
                    {
                      name: 'currency',
                      label: 'Currency',
                      type: 'currency',
                    },
                  ],
                  visible: step === 1 && values.discountType === 'amountOff',
                },
                {
                  type: 'columns',
                  columns: 2,
                  gap: 'sm',
                  fields: [
                    {
                      name: 'percentOff',
                      label: 'Discount %',
                      required: step === 1 && values.discountType === 'percentOff',
                      showRequired: true,
                      type: 'number',
                      placeholder: 'Discount %',
                    },
                  ],
                  visible: step === 1 && values.discountType === 'percentOff',
                },
                { type: 'divider', id: 'discount-divider-bottom', visible: step === 1 },
                {
                  type: 'custom',
                  id: 'subscriptions-heading',
                  renderField: () => <SubsectionTitle>Subscriptions</SubsectionTitle>,
                  reduceMargin: true,
                  visible: step === 1,
                },
                {
                  type: 'select',
                  name: 'duration',
                  label: COUPON_DURATION_LABEL,
                  options: COUPON_DURATION_OPTIONS,
                  visible: step === 1,
                },
                {
                  type: 'columns',
                  columns: 2,
                  fields: [
                    {
                      type: 'number',
                      label: 'Number of months',
                      name: 'durationInMonths',
                      helpText: 'How many months the discount will apply.',
                    },
                  ],
                  visible: step === 1 && values.duration === 'repeating',
                },
                {
                  type: 'checkbox',
                  name: 'appliesToSetupFee',
                  label: 'Setup fee',
                  helpText: 'Discounts the setup fee as well as the price.',
                  visible: step === 1,
                },
                {
                  type: 'custom',
                  id: 'events-heading',
                  renderField: () => <SubsectionTitle>Events</SubsectionTitle>,
                  reduceMargin: true,
                  visible: step === 1,
                },
                {
                  type: 'checkbox',
                  name: 'appliesToTicketFees',
                  label: 'Booking fees',
                  helpText: "Discounts each ticket's booking fee as well as its price.",
                  visible: step === 1,
                },
                ...scopeFields({ visible: step === 2, limitScope: values.limitScope }),
                { type: 'divider', id: 'limits-divider', visible: step === 2 },
                {
                  type: 'number',
                  placeholder: 'Unlimited',
                  label: 'Max uses',
                  helpText:
                    'Total uses across all customers. One customer can use it more than once.',
                  name: 'maxRedemptions',
                  visible: step === 2,
                },
                ...expiryFields({ visible: step === 2, values }),
              ];
            }}
          >
            {step === LAST_STEP && <CouponReview onEdit={goToStep} />}
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default DiscountsCouponsCreate;
